"use client";

import { useMemo } from "react";
import { SafeImage } from "@/components/ui/safe-image";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { ProductVariant } from "@/types";
import { cn, mediaUrl } from "@/lib/utils";

interface ProductVariantsProps {
  variants: ProductVariant[];
  selectedId?: string;
  onSelect: (variant: ProductVariant) => void;
}

export function ProductVariants({ variants, selectedId, onSelect }: ProductVariantsProps) {
  const optionGroups = useMemo(() => {
    const groups: Record<string, { value: string; variants: ProductVariant[] }[]> = {};
    for (const variant of variants) {
      for (const opt of variant.options) {
        if (!groups[opt.name]) groups[opt.name] = [];
        const existing = groups[opt.name].find((g) => g.value === opt.value);
        if (existing) {
          existing.variants.push(variant);
        } else {
          groups[opt.name].push({ value: opt.value, variants: [variant] });
        }
      }
    }
    return groups;
  }, [variants]);

  if (variants.length === 0) return null;

  return (
    <TooltipProvider delayDuration={150} skipDelayDuration={100}>
      <div className="space-y-4">
        {Object.entries(optionGroups).map(([optionName, groups]) => {
          // Bu grupta en az bir varyantın resmi varsa görsel mod kullan
          const hasImages = groups.some(g => g.variants[0]?.imageUrl);

          return (
            <div key={optionName}>
              <p className="text-sm font-medium text-gray-700 mb-2">{optionName}</p>
              <div className="flex flex-wrap gap-2">
                {groups.map(({ value, variants: groupVariants }) => {
                  const isSelected = groupVariants.some((v) => v.id === selectedId);
                  const isAvailable = groupVariants.some((v) => v.isActive && v.stock > 0);
                  const representative = groupVariants[0];
                  const imgUrl = representative?.imageUrl;

                  if (hasImages && imgUrl) {
                    const triggerButton = (
                      <button
                        onClick={() => !isSelected && isAvailable && onSelect(representative)}
                        disabled={!isAvailable}
                        aria-label={value}
                        className={cn(
                          "relative h-14 w-14 overflow-hidden rounded-[var(--sf-radius)] border-2 transition-all",
                          isSelected
                            ? "border-[var(--sf-primary)] ring-2 ring-[var(--sf-primary)]/30"
                            : isAvailable
                            ? "border-gray-200 hover:border-[var(--sf-primary)]"
                            : "cursor-not-allowed border-gray-100 opacity-40"
                        )}
                      >
                        <SafeImage
                          src={mediaUrl(imgUrl)}
                          alt={value}
                          fill
                          className="object-cover"
                          sizes="56px"
                          fallbackClassName="bg-gray-50 text-gray-300"
                        />
                        {!isAvailable && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                            <div className="h-[1px] w-8 rotate-45 bg-gray-400" />
                          </div>
                        )}
                      </button>
                    );

                    return (
                      <Tooltip key={value}>
                        {isAvailable ? (
                          <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
                        ) : (
                          <TooltipTrigger asChild>
                            <span className="inline-flex">{triggerButton}</span>
                          </TooltipTrigger>
                        )}
                        <TooltipContent side="top">{value}</TooltipContent>
                      </Tooltip>
                    );
                  }

                  // Metin buton (resim yoksa)
                  return (
                    <button
                      key={value}
                      onClick={() => !isSelected && isAvailable && onSelect(representative)}
                      disabled={!isAvailable}
                      className={cn(
                        "rounded-[var(--sf-radius)] border px-3 py-1.5 text-sm transition-colors",
                        isSelected
                          ? "border-[var(--sf-primary)] bg-[var(--sf-primary)] text-white"
                          : isAvailable
                          ? "border-gray-300 text-gray-700 hover:border-[var(--sf-primary)]"
                          : "cursor-not-allowed border-gray-200 text-gray-300 line-through"
                      )}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
